import { V2Button, Icon } from '@sakhtar/design-system/react';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import { formatRialFa, parseRialInput, type AmountDisplayUnit } from '../../lib/finance-display.js';
import type { InvoicePreview } from '../../lib/invoice-preview.js';
import type { MasterDataRecord } from '../../lib/types/masterData.js';
import type { AccountListItem, OpenItemChargeType } from '../../lib/types/finance.js';
import { CHARGE_TYPE_LABEL } from '../../lib/types/finance.js';

export interface InvoiceFormLine {
  readonly lineKind: 'item' | 'charge';
  readonly chargeType: OpenItemChargeType | '';
  readonly productId: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPriceRial: string;
  readonly discountRial: string;
  /** Empty = the invoice's counterpart account. */
  readonly accountId: string;
}

export const EMPTY_INVOICE_LINE: InvoiceFormLine = {
  lineKind: 'item',
  chargeType: '',
  productId: '',
  description: '',
  quantity: '1',
  unitPriceRial: '',
  discountRial: '',
  accountId: '',
};

/** "9%" from a master-data tax row's exact ratio (display only). */
export function taxRateLabel(tax: MasterDataRecord): string {
  const num = Number(tax.rateNumerator);
  const den = Number(tax.rateDenominator);
  if (!den) return '—';
  const percent = (num * 100) / den;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}٪`;
}

function quantityInput(value: string): string {
  return toEnglishDigits(value)
    .replace(/[٫,]/g, '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1');
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html — AR/AP manual invoice (فاکتور دستی)
 * archetype: ENTITY_CREATE_EDIT line-item sub-table
 * status: KIT_CONFORMANT — goods/service rows (quantity × unit price, row
 *   discount), side-charge rows (freight, warehousing, insurance) with an
 *   optional own GL account, an invoice-level discount, and a live totals
 *   strip. The strip is a BigInt preview of the server's own calculation
 *   (lib/invoice-preview.ts); the server recomputes and is authoritative.
 */
export function InvoiceLinesEditor(props: {
  readonly kind: 'ar' | 'ap';
  readonly lines: readonly InvoiceFormLine[];
  readonly preview: InvoicePreview;
  readonly unit: AmountDisplayUnit;
  readonly unitLabel: string;
  readonly accounts: readonly AccountListItem[];
  readonly products: readonly MasterDataRecord[];
  readonly documentDiscount: string;
  readonly onDocumentDiscount: (value: string) => void;
  readonly onChange: (index: number, patch: Partial<InvoiceFormLine>) => void;
  readonly onAdd: (lineKind: 'item' | 'charge') => void;
  readonly onRemove: (index: number) => void;
}) {
  const { lines, preview, unit, unitLabel } = props;
  const fmt = (v: bigint) => formatRialFa(v.toString(), unit);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        border: '1px solid var(--sk-border)',
        borderRadius: 'var(--sk-r-md)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid var(--sk-divider)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>اقلام فاکتور</span>
        <V2Button
          type="button"
          size="sm"
          variant="secondary"
          icon="plus"
          onClick={() => props.onAdd('item')}
        >
          ردیف کالا/خدمت
        </V2Button>
        <V2Button
          type="button"
          size="sm"
          variant="secondary"
          icon="plus"
          onClick={() => props.onAdd('charge')}
        >
          {props.kind === 'ap' ? 'هزینهٔ جانبی خرید (حمل، انبارداری…)' : 'هزینهٔ جانبی (حمل…)'}
        </V2Button>
      </div>
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th style={{ minWidth: 200 }}>شرح / کالا</th>
              <th style={{ width: 90 }}>مقدار</th>
              <th style={{ width: 130 }}>فی (ریال)</th>
              <th style={{ width: 120 }}>تخفیف ردیف (ریال)</th>
              <th style={{ width: 170 }}>حساب (اختیاری)</th>
              <th style={{ width: 120 }}>مبلغ ({unitLabel})</th>
              <th style={{ width: 110 }}>ارزش افزوده</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const p = preview.lines[i];
              return (
                <tr key={i}>
                  <td className="sk-num">{i + 1}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {line.lineKind === 'charge' ? (
                        <select
                          className="sk-select"
                          aria-label="نوع هزینهٔ جانبی"
                          value={line.chargeType}
                          onChange={(e) => {
                            const chargeType = e.target.value as OpenItemChargeType;
                            props.onChange(i, {
                              chargeType,
                              description: CHARGE_TYPE_LABEL[chargeType] ?? line.description,
                            });
                          }}
                        >
                          {(Object.keys(CHARGE_TYPE_LABEL) as OpenItemChargeType[]).map((c) => (
                            <option key={c} value={c}>
                              {CHARGE_TYPE_LABEL[c]}
                            </option>
                          ))}
                        </select>
                      ) : props.products.length > 0 ? (
                        <select
                          className="sk-select"
                          aria-label="کالا"
                          value={line.productId}
                          onChange={(e) => {
                            const product = props.products.find(
                              (pr) => String(pr.productId) === e.target.value,
                            );
                            props.onChange(i, {
                              productId: e.target.value,
                              ...(product && !line.description
                                ? { description: String(product.name) }
                                : {}),
                            });
                          }}
                        >
                          <option value="">— کالای تعریف‌شده (اختیاری) —</option>
                          {props.products.map((pr) => (
                            <option key={String(pr.productId)} value={String(pr.productId)}>
                              {String(pr.code)} — {String(pr.name)}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <input
                        className="sk-input"
                        aria-label="شرح ردیف"
                        placeholder="شرح"
                        value={line.description}
                        onChange={(e) => props.onChange(i, { description: e.target.value })}
                      />
                    </div>
                  </td>
                  <td>
                    <input
                      className="sk-input ltr sk-num"
                      inputMode="decimal"
                      aria-label="مقدار"
                      value={line.quantity}
                      onChange={(e) =>
                        props.onChange(i, { quantity: quantityInput(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="sk-input ltr sk-num"
                      inputMode="numeric"
                      aria-label="فی"
                      value={line.unitPriceRial}
                      onChange={(e) =>
                        props.onChange(i, { unitPriceRial: parseRialInput(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    {line.lineKind === 'item' ? (
                      <input
                        className="sk-input ltr sk-num"
                        inputMode="numeric"
                        aria-label="تخفیف ردیف"
                        value={line.discountRial}
                        onChange={(e) =>
                          props.onChange(i, { discountRial: parseRialInput(e.target.value) })
                        }
                      />
                    ) : (
                      <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <select
                      className="sk-select"
                      aria-label="حساب ردیف"
                      value={line.accountId}
                      onChange={(e) => props.onChange(i, { accountId: e.target.value })}
                    >
                      <option value="">حساب مقابل فاکتور</option>
                      {props.accounts.map((a) => (
                        <option key={a.accountId} value={a.accountId}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="sk-num">
                    {p?.error ? (
                      <span style={{ color: 'var(--sk-danger)', fontSize: 12 }}>{p.error}</span>
                    ) : (
                      fmt(p?.taxableRial ?? 0n)
                    )}
                  </td>
                  <td className="sk-num">{fmt(p?.vatRial ?? 0n)}</td>
                  <td>
                    {lines.length > 1 ? (
                      <button
                        type="button"
                        className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                        style={{ color: 'var(--sk-danger)' }}
                        aria-label="حذف ردیف"
                        onClick={() => props.onRemove(i)}
                      >
                        <Icon name="x" size="sm" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
          padding: '10px 14px 14px',
          fontSize: 13,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          تخفیف کل فاکتور (ریال)
          <input
            className="sk-input ltr sk-num"
            inputMode="numeric"
            value={props.documentDiscount}
            onChange={(e) => props.onDocumentDiscount(parseRialInput(e.target.value))}
          />
        </label>
        <Total label="جمع اقلام" value={fmt(preview.grossRial)} unitLabel={unitLabel} />
        <Total label="جمع تخفیف" value={fmt(preview.discountRial)} unitLabel={unitLabel} />
        <Total label="هزینه‌های جانبی" value={fmt(preview.chargesRial)} unitLabel={unitLabel} />
        <Total label="مبلغ مشمول مالیات" value={fmt(preview.taxableRial)} unitLabel={unitLabel} />
        <Total label="ارزش افزوده" value={fmt(preview.vatRial)} unitLabel={unitLabel} />
        <Total label="جمع کل فاکتور" value={fmt(preview.totalRial)} unitLabel={unitLabel} strong />
      </div>
      <div style={{ padding: '0 14px 12px', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
        پیش‌نمایش؛ مبالغ قطعی را سرور با همین قواعد (گرد کردن نیم به بالا) محاسبه و ثبت می‌کند.
      </div>
    </div>
  );
}

function Total(props: {
  readonly label: string;
  readonly value: string;
  readonly unitLabel: string;
  readonly strong?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color: 'var(--sk-fg-muted)' }}>{props.label}</span>
      <span className="sk-num" style={{ fontWeight: props.strong ? 700 : 600, fontSize: 15 }}>
        {props.value} <span style={{ fontSize: 12, fontWeight: 400 }}>{props.unitLabel}</span>
      </span>
    </div>
  );
}
